import {DAY_NAMES, OFFER_CATEGORIES, OFFER_TEMPLATES, type Offer} from '@chekin/core';
import {parseTimeFromName} from '@chekin/shared/payments';
import {toISODate} from 'utils/upselling';

const WEEKDAYS = [
  DAY_NAMES.sunday,
  DAY_NAMES.monday,
  DAY_NAMES.tuesday,
  DAY_NAMES.wednesday,
  DAY_NAMES.thursday,
  DAY_NAMES.friday,
  DAY_NAMES.saturday,
];

export type RegistrationDeal<Partner> =
  | {kind: 'offer'; offer: Offer}
  | {kind: 'partner'; partner: Partner};

type SelectRegistrationDealProps<Partner> = {
  offers: Offer[];
  rankedOffers: Offer[];
  bookedOfferIds: Set<string>;
  checkInDate?: string | null;
  checkOutDate?: string | null;
  esim?: Partner;
  transfer?: Partner;
  tour?: Partner;
  now?: Date;
};

type StayOffer = Pick<Offer, 'category' | 'template'>;

const isEarlyCheckInOffer = (offer: StayOffer) =>
  offer.category === OFFER_CATEGORIES.checkIn ||
  offer.template === OFFER_TEMPLATES.checkIn;

const isLateCheckOutOffer = (offer: StayOffer) =>
  offer.category === OFFER_CATEGORIES.checkOut ||
  offer.template === OFFER_TEMPLATES.checkOut;

const getLatestSlot = (offer: Offer) =>
  (offer.items ?? [])
    .map(item => parseTimeFromName(item.name ?? ''))
    .filter((slot): slot is string => Boolean(slot))
    .sort()
    .pop();

function fitsDay(offer: Offer, date: string | null | undefined, now: Date) {
  const day = date?.slice(0, 10);
  const today = toISODate(now);
  if (!day || day < today) return false;

  const windows = offer.availability?.duration_availabilities ?? [];
  const weekday = WEEKDAYS[new Date(`${day}T00:00:00`).getDay()];
  if (windows.length && !windows.some(window => window.weekday === weekday)) {
    return false;
  }

  if (day > today) return true;
  const latestSlot = getLatestSlot(offer);
  const currentTime = now.toTimeString().slice(0, 5);
  return !latestSlot || latestSlot > currentTime;
}

function selectRegistrationDeal<Partner>({
  offers,
  rankedOffers,
  bookedOfferIds,
  checkInDate,
  checkOutDate,
  esim,
  transfer,
  tour,
  now = new Date(),
}: SelectRegistrationDealProps<Partner>): RegistrationDeal<Partner> | null {
  const isBookable = (offer: Offer) =>
    offer.is_active !== false && !bookedOfferIds.has(offer.id);
  const bookable = offers.filter(isBookable);
  const asOffer = (offer: Offer) => ({kind: 'offer', offer} as const);
  const asPartner = (partner: Partner) => ({kind: 'partner', partner} as const);

  const earlyCheckIn = bookable.find(
    offer => isEarlyCheckInOffer(offer) && fitsDay(offer, checkInDate, now),
  );
  if (earlyCheckIn) return asOffer(earlyCheckIn);

  const lateCheckOut = bookable.find(
    offer => isLateCheckOutOffer(offer) && fitsDay(offer, checkOutDate, now),
  );
  if (lateCheckOut) return asOffer(lateCheckOut);

  if (esim) return asPartner(esim);
  if (transfer) return asPartner(transfer);

  const hostTransfer = bookable.find(
    offer => offer.category === OFFER_CATEGORIES.transportation,
  );
  if (hostTransfer) return asOffer(hostTransfer);

  if (tour) return asPartner(tour);

  const next = rankedOffers.find(
    offer =>
      isBookable(offer) && !isEarlyCheckInOffer(offer) && !isLateCheckOutOffer(offer),
  );
  return next ? asOffer(next) : null;
}

export {isEarlyCheckInOffer, isLateCheckOutOffer, selectRegistrationDeal};
